import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Users, Briefcase, Eye, Download, Mail, Star, Clock3, Share2, ImageDown, Sparkles } from "lucide-react";

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [share, setShare] = useState(null); // {job, caption}
  const [summary, setSummary] = useState(null); // {name, text}
  const [summaryLoading, setSummaryLoading] = useState(false);

  const genSummary = async (a) => {
    setSummary({ name: a.full_name, text: "" });
    setSummaryLoading(true);
    try {
      const { data } = await api.post("/ai/resume-summary", { application_id: a.application_id });
      setSummary({ name: a.full_name, text: data.summary });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not summarize"); setSummary(null); }
    finally { setSummaryLoading(false); }
  };

  const openShare = async (job) => {
    setShare({ job, caption: "Loading caption…" });
    try {
      const { data } = await api.get(`/employer/social/job/${job.job_id}/caption`);
      setShare({ job, caption: data.caption });
    } catch { setShare({ job, caption: "Could not load caption." }); }
  };

  const load = () => { setLoading(true); api.get("/employer/jobs").then(({ data }) => setJobs(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const viewApps = async (job) => {
    setOpenJob(job);
    const { data } = await api.get(`/employer/jobs/${job.job_id}/applications`);
    setApps(data);
  };

  const toggleRenew = async (job, v) => {
    await api.patch(`/employer/jobs/${job.job_id}/auto-renew`, { auto_renew: v });
    setJobs((prev) => prev.map((j) => (j.job_id === job.job_id ? { ...j, auto_renew: v } : j)));
    toast.success(v ? "Auto-renew enabled" : "Auto-renew disabled");
  };

  const featureJob = async (job) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "featured_upgrade", origin_url: window.location.origin, purpose: "featured", job_id: job.job_id,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  const downloadResume = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open file"); }
  };

  const statusBadge = (s) => {
    const map = { active: "bg-emerald-100 text-emerald-700", draft: "bg-slate-100 text-slate-600", expired: "bg-slate-100 text-slate-400", suspended: "bg-red-100 text-red-700", flagged: "bg-amber-100 text-amber-700" };
    return <Badge className={`${map[s] || "bg-slate-100"} hover:${map[s]}`}>{s}</Badge>;
  };

  const active = jobs.filter((j) => j.status === "active");
  const others = jobs.filter((j) => j.status !== "active");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 flex-1 w-full">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div><h1 className="font-display text-3xl font-extrabold text-navy-800">Employer dashboard</h1><p className="text-slate-500 mt-1">Manage your listings and review applicants.</p></div>
          <Button data-testid="post-new-job-btn" onClick={() => navigate("/post-job")} className="bg-navy-800 hover:bg-navy-900 text-white gap-2"><Plus className="h-4 w-4" /> Post a job</Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Stat icon={Briefcase} label="Active listings" value={active.length} />
          <Stat icon={Users} label="Total applicants" value={jobs.reduce((a, j) => a + (j.application_count || 0), 0)} />
          <Stat icon={Eye} label="Total views" value={jobs.reduce((a, j) => a + (j.views || 0), 0)} />
        </div>

        {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div> : (
          <Tabs defaultValue="active">
            <TabsList><TabsTrigger value="active" data-testid="tab-active">Active ({active.length})</TabsTrigger><TabsTrigger value="other" data-testid="tab-other">Draft / Expired ({others.length})</TabsTrigger></TabsList>
            <TabsContent value="active"><JobTable jobs={active} {...{ statusBadge, viewApps, toggleRenew, featureJob, openShare }} /></TabsContent>
            <TabsContent value="other"><JobTable jobs={others} {...{ statusBadge, viewApps, toggleRenew, featureJob, openShare }} showPay /></TabsContent>
          </Tabs>
        )}
      </div>

      <Dialog open={!!openJob} onOpenChange={(v) => !v && setOpenJob(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Applicants — {openJob?.job_title}</DialogTitle><DialogDescription>Review applicants and generate AI summaries for this listing.</DialogDescription></DialogHeader>
          {apps.length === 0 ? <p className="text-sm text-slate-500 py-8 text-center">No applications yet.</p> : (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Applied</TableHead><TableHead>Files</TableHead></TableRow></TableHeader>
                <TableBody>
                  {apps.map((a) => (
                    <TableRow key={a.application_id} data-testid={`applicant-row-${a.application_id}`}>
                      <TableCell><div className="font-medium text-navy-800">{a.full_name}</div><div className="text-xs text-slate-400">{a.location}</div></TableCell>
                      <TableCell className="text-sm"><div>{a.email}</div><div className="text-slate-400">{a.phone}</div></TableCell>
                      <TableCell className="text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString()}<div>{a.email_status === "failed" ? <span className="text-red-500 flex items-center gap-1"><Mail className="h-3 w-3" /> email failed</span> : <span className="text-emerald-600">emailed</span>}</div></TableCell>
                      <TableCell className="space-x-2">
                        <Button size="sm" variant="ghost" onClick={() => genSummary(a)} data-testid={`ai-summary-${a.application_id}`} className="text-amber-700 hover:bg-amber-50 gap-1"><Sparkles className="h-3.5 w-3.5" /> AI summary</Button>
                        {a.resume_file_id && <Button size="sm" variant="outline" onClick={() => downloadResume(a.resume_file_id)} data-testid={`resume-${a.application_id}`}>Resume</Button>}
                        {a.cover_letter_file_id && <Button size="sm" variant="ghost" onClick={() => downloadResume(a.cover_letter_file_id)}>Cover</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!share} onOpenChange={(v) => !v && setShare(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Share this job</DialogTitle></DialogHeader>
          {share && (
            <div className="space-y-4">
              <img src={`${API}/employer/social/job/${share.job.job_id}.png`} alt="Share card" className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="share-image-preview" />
              <div>
                <label className="text-sm font-semibold text-navy-800">Caption</label>
                <Textarea value={share.caption} readOnly rows={7} className="mt-1.5 text-sm" data-testid="share-caption" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button onClick={() => { navigator.clipboard.writeText(share.caption); toast.success("Caption copied"); }} data-testid="copy-share-caption" className="bg-navy-800 hover:bg-navy-900 text-white">Copy caption</Button>
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/jobs/${share.job.job_id}`); toast.success("Link copied"); }} data-testid="copy-share-link">Copy link</Button>
                <Button variant="outline" onClick={() => window.open(`${API}/employer/social/job/${share.job.job_id}.png`, "_blank")} data-testid="open-share-image" className="gap-1.5"><ImageDown className="h-4 w-4" /> Image</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!summary} onOpenChange={(v) => !v && setSummary(null)}>
        <DialogContent className="max-w-lg" data-testid="summary-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /> AI summary — {summary?.name}</DialogTitle><DialogDescription>An AI-generated snapshot of this candidate's safety experience.</DialogDescription></DialogHeader>
          {summaryLoading ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div> : <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="summary-text">{summary?.text}</div>}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex items-center gap-4">
    <div className="h-11 w-11 rounded-lg bg-amber-100 flex items-center justify-center"><Icon className="h-5 w-5 text-amber-600" /></div>
    <div><div className="font-display text-2xl font-extrabold text-navy-800">{value}</div><div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div></div>
  </div>
);

const JobTable = ({ jobs, statusBadge, viewApps, toggleRenew, featureJob, openShare, showPay }) => {
  const navigate = useNavigate();
  if (jobs.length === 0) return <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300 mt-4 text-slate-500">No listings here.</div>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-4 overflow-hidden">
      <Table>
        <TableHeader><TableRow><TableHead>Job</TableHead><TableHead>Status</TableHead><TableHead>Applicants</TableHead><TableHead>Days left</TableHead><TableHead>Auto-renew</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {jobs.map((j) => (
            <TableRow key={j.job_id} data-testid={`employer-job-${j.job_id}`}>
              <TableCell>
                <div className="font-medium text-navy-800 flex items-center gap-2">
                  {j.job_title}
                  {j.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-600" data-testid={`emp-featured-${j.job_id}`}><Star className="h-3 w-3" fill="currentColor" /> Featured</span>}
                </div>
                <div className="text-xs text-slate-400">{j.remote ? "Remote" : `${j.city}, ${j.state}`}</div>
                {(j.featured_views || j.featured_apps) ? (
                  <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`featured-stats-${j.job_id}`}>
                    <Star className="h-3 w-3" fill="currentColor" /> {j.featured_views || 0} views · {j.featured_apps || 0} applicants while featured
                  </div>
                ) : null}
              </TableCell>
              <TableCell>{statusBadge(j.status)}</TableCell>
              <TableCell><Badge variant="secondary">{j.application_count || 0}</Badge></TableCell>
              <TableCell className="text-sm text-slate-500">{typeof j.days_left === "number" ? `${j.days_left}d` : "—"}</TableCell>
              <TableCell>{j.status === "active" ? <Switch checked={!!j.auto_renew} onCheckedChange={(v) => toggleRenew(j, v)} data-testid={`renew-${j.job_id}`} /> : "—"}</TableCell>
              <TableCell className="text-right space-x-2 whitespace-nowrap">
                {showPay && (j.status === "draft" || j.status === "expired") ? (
                  <Button size="sm" onClick={() => navigate(`/renew/job/${j.job_id}`)} data-testid={`renew-pay-${j.job_id}`} className="bg-amber-500 hover:bg-amber-600 text-navy-900">Renew $99</Button>
                ) : (
                  <>
                    {j.status === "active" && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/renew/job/${j.job_id}`)} data-testid={`renew-early-${j.job_id}`} className="text-emerald-700 hover:bg-emerald-50 gap-1"><Clock3 className="h-3.5 w-3.5" /> Renew early $79</Button>
                    )}
                    {j.status === "active" && !j.featured && (
                      <Button size="sm" variant="outline" onClick={() => featureJob(j)} data-testid={`feature-${j.job_id}`} className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-1"><Star className="h-3.5 w-3.5" /> Feature $49</Button>
                    )}
                    {j.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => openShare(j)} data-testid={`share-${j.job_id}`} className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-1"><Share2 className="h-3.5 w-3.5" /> Share</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => viewApps(j)} data-testid={`view-applicants-${j.job_id}`}>View applicants</Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
